import { gql } from "@apollo/client";

/**
 * Per-resource config for the document-queue entity pickers: search query (for
 * autocomplete) + by-ID query (for name resolution). Shared by EntitySelector
 * and the inline create dialog's parent-firm picker.
 */
export const ENTITY_CONFIG: Record<
  string,
  {
    searchQuery: ReturnType<typeof gql>;
    getSearchResults: (data: any) => any[];
    byIdQuery: ReturnType<typeof gql>;
    byIdPath: string;
  }
> = {
  firm: {
    searchQuery: gql`
      query GetFirmsBySearch($searchTerm: String!) {
        firmList(
          page: { limit: 15, offset: 0 }
          sort: [{ field: nameSearchRank, order: ASC }]
          filter: { q: $searchTerm }
        ) {
          items {
            id
            name
          }
        }
      }
    `,
    getSearchResults: data => data?.firmList?.items ?? [],
    byIdQuery: gql`
      query GetFirmById($id: Int!) {
        firm(id: $id) {
          id
          name
        }
      }
    `,
    byIdPath: "firm",
  },
  investment: {
    searchQuery: gql`
      query GetInvestmentsBySearch($searchTerm: String!) {
        investmentList(
          page: { limit: 15 }
          searchFilters: [{ SEARCH_TERM: { value: $searchTerm } }]
        ) {
          items {
            id
            name
            firm {
              id
              name
            }
          }
        }
      }
    `,
    getSearchResults: data => data?.investmentList?.items ?? [],
    byIdQuery: gql`
      query GetInvestmentById($id: Int!) {
        investment(id: $id) {
          id
          name
          firm {
            id
            name
          }
        }
      }
    `,
    byIdPath: "investment",
  },
  company: {
    searchQuery: gql`
      query GetCompaniesBySearch($searchTerm: String!) {
        companyList(
          page: { limit: 15, offset: 0 }
          sort: [{ field: nameSearchRank, order: ASC }]
          filter: { q: $searchTerm }
        ) {
          items {
            id
            name
          }
        }
      }
    `,
    getSearchResults: data => data?.companyList?.items ?? [],
    byIdQuery: gql`
      query GetCompanyById($id: Int!) {
        company(id: $id) {
          id
          name
        }
      }
    `,
    byIdPath: "company",
  },
  deal: {
    searchQuery: gql`
      query GetDealsBySearch($searchTerm: String!) {
        dealList(page: { limit: 15 }, filter: { q: $searchTerm }) {
          items {
            id
            name
          }
        }
      }
    `,
    getSearchResults: data => data?.dealList?.items ?? [],
    byIdQuery: gql`
      query GetDealById($id: Int!) {
        deal(id: $id) {
          id
          name
        }
      }
    `,
    byIdPath: "deal",
  },
  firmEngagement: {
    searchQuery: gql`
      query GetFirmEngagementsBySearch($searchTerm: String!) {
        firmEngagementList(
          page: { limit: 15, offset: 0 }
          sort: [{ field: nameSearchRank, order: ASC }]
          filter: { q: $searchTerm }
        ) {
          items {
            id
            name
            firm {
              id
              name
            }
          }
        }
      }
    `,
    getSearchResults: data => data?.firmEngagementList?.items ?? [],
    byIdQuery: gql`
      query GetFirmEngagementById($id: Int!) {
        firmEngagement(id: $id) {
          id
          name
          firm {
            id
            name
          }
        }
      }
    `,
    byIdPath: "firmEngagement",
  },
};

export const CAN_CREATE_ENTITY = gql`
  query entityCreateCanAccess($mutationName: String!, $mutationArgs: String!) {
    userMutationAccessible(
      mutationName: $mutationName
      mutationArgs: $mutationArgs
    ) {
      canAccess
    }
  }
`;

/**
 * Near-match lookups reuse the fStringSimilarity ranking added for the Profile
 * Builder firm fuzzy match (#3860 / #3750) rather than adding a second matching
 * implementation.
 */
export const FIRM_NEAR_MATCHES = gql`
  query entityCreateFirmNearMatches($name: String!, $limit: Int!) {
    firmList(
      filter: { fuzzyTerm: $name }
      sort: [{ field: fuzzyRank, order: DESC }]
      page: { limit: $limit, offset: 0 }
    ) {
      items {
        id
        name
      }
    }
  }
`;

export const INVESTMENT_NEAR_MATCHES = gql`
  query entityCreateInvestmentNearMatches($name: String!, $limit: Int!) {
    investmentList(
      searchFilters: [{ FUZZY_TERM: { value: $name } }]
      sort: [{ field: fuzzyRank, order: DESC }]
      page: { limit: $limit, offset: 0 }
    ) {
      items {
        id
        name
        firm {
          id
          name
        }
      }
    }
  }
`;

export const INVESTMENT_CREATE_ENUMS = gql`
  query entityCreateInvestmentEnums {
    # No availableOnCreation filter: passing it true drops the "id != 4"
    # predicate and offers the Inactive list status, which the Data Collection
    # create form deliberately hides (only its Edit form passes the filter).
    investmentListEnumList(
      sort: [{ field: name, order: ASC }]
      page: { limit: 200, offset: 0 }
    ) {
      items {
        id
        name
      }
    }
    assetClassEnumList(
      sort: [{ field: displayOrder, order: ASC }]
      page: { limit: 200, offset: 0 }
    ) {
      items {
        id
        name
      }
    }
    strategyEnumList(
      sort: [{ field: name, order: ASC }]
      page: { limit: 500, offset: 0 }
    ) {
      items {
        id
        name
      }
    }
  }
`;

export const ADD_FIRM = gql`
  mutation entityCreateAddFirm($input: FirmInput!) {
    addFirm(input: $input) {
      id
      name
    }
  }
`;

export const ADD_INVESTMENT = gql`
  mutation entityCreateAddInvestment($input: InvestmentInput!) {
    addInvestment(input: $input) {
      id
      name
      firm {
        id
        name
      }
    }
  }
`;
